import { darken, Theme } from '@mui/material/styles';
import { alternatingRowClassName, rowHeight } from 'utils/staticOptions';

export default function DataGrid(theme: Theme) {
  const light = theme.palette.mode === 'light';
  const base = theme.palette.background.default;
  // Zebra striping. Light mode tints only the even rows (gentle). Dark mode lifts the rows toward a
  // soft charcoal (~#1e1e1e) instead of near-black, with the even rows a touch lighter so the stripe
  // still reads. Hover is the brightest/strongest in each mode.
  const evenColor = light ? darken(base, 0.03) : '#1e1e1e';
  const oddColor = light ? 'transparent' : '#181818';
  const hoverColor = light ? darken(base, 0.05) : '#2a2a2a';

  return {
    MuiDataGrid: {
      defaultProps: {
        hideFooter: true,
        rowHeight: rowHeight,
        columnHeaderHeight: rowHeight,
        // Global zebra striping. Grids that need their own getRowClassName override this default —
        // compose with withAlternatingRow() to keep striping. Colors live in styleOverrides below.
        getRowClassName: alternatingRowClassName
      },
      styleOverrides: {
        root: {
          border: 'none',
          '& *:not(svg)': { fontSize: '0.75rem' },
          '& .MuiSvgIcon-fontSizeSmall': { fontSize: '1.25rem' },
          '& .anticon': { fontSize: '0.85rem' },
          // Kill the focus ring everywhere — plain :focus on text cells and :focus-within on cells/headers
          // that hold a focusable control (checkbox column, master-detail expand column, etc.).
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
          '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
          '& .MuiDataGrid-cell--editing:focus-within': { outline: 'none !important' },
          '& .MuiInputBase-root': {
            borderRadius: 0,
            boxShadow: 'none !important',
            fontSize: 'unset'
          },
          '& .MuiOutlinedInput-root': {
            '& fieldset': { border: 'none', boxShadow: 'none !important' },
            '&:hover fieldset': { border: 'none' },
            '&.Mui-focused fieldset': { border: 'none' }
          },
          '& .MuiDataGrid-booleanCell': {
            color: `${theme.palette.success.main} !important`,
            fontSize: '1.4rem'
          },
          '& .hidden .MuiDataGrid-booleanCell': { display: 'none' },
          '& .anticon-check-square': { top: '0 !important', left: '0 !important' },
          // Opt-in zebra striping. A grid turns it on by tagging rows 'even'/'odd' via
          // getRowClassName={alternatingRowClassName}. Class-based (not :nth-child) so it survives
          // virtualization and master-detail panels. Hover stays highlighted on both even and odd rows.
          // `:not(.Mui-selected)` keeps the stripe from painting over the selected-row primary overlay.
          '& .MuiDataGrid-row.even:not(.Mui-selected)': {
            backgroundColor: evenColor,
            '&:hover': { backgroundColor: hoverColor }
          },
          '& .MuiDataGrid-row.odd:not(.Mui-selected)': {
            backgroundColor: oddColor,
            '&:hover': { backgroundColor: hoverColor }
          }
        }
      }
    }
  };
}
